// Cai dat luu tru: giu toi da bao nhieu san pham, don tu dong hay khong.
// Luon cho xem truoc chinh xac nhung gi se bi xoa truoc khi xoa that.

import { useEffect, useState } from "react";
import { api } from "../api";
import { Alert, Badge, Empty, ErrorBox, Modal, Spinner, Switch } from "../components/ui";
import ChoPhepCard from "../components/ChoPhep";
import type { CaiDat, KeHoachDon, ThongKeKho } from "../types";

function co(n: number): string {
  if (n >= 1048576) return (n / 1048576).toFixed(1) + " MB";
  if (n >= 1024) return Math.round(n / 1024) + " KB";
  return n + " B";
}

function ChiTietGiu({ kh }: { kh: KeHoachDon }) {
  const phan = Object.entries(kh.tong_theo_thu_muc ?? {});
  if (!phan.length) return null;
  return (
    <div className="small muted" style={{ marginTop: 6 }}>
      Trần áp riêng cho từng giai đoạn:{" "}
      {phan.map(([ten, n], i) => (
        <span key={ten}>
          {i > 0 ? " · " : ""}
          {ten} {n}/{kh.gioi_han_san_pham}
        </span>
      ))}
    </div>
  );
}

function BangXoa({ kh }: { kh: KeHoachDon }) {
  if (!kh.se_xoa.length)
    return (
      <>
        <Alert tone="ok">
          Không có gì để xoá. Đang giữ {kh.tong_dang_giu} / {kh.gioi_han_san_pham} sản phẩm.
        </Alert>
        <ChiTietGiu kh={kh} />
      </>
    );
  return (
    <>
      <Alert tone="warn">
        Sẽ xoá <strong>{kh.so_file_xoa} file</strong>, thu hồi{" "}
        <strong>{co(kh.bytes_thu_hoi)}</strong>. Còn giữ {kh.tong_dang_giu} /{" "}
        {kh.gioi_han_san_pham} sản phẩm.
      </Alert>
      <ChiTietGiu kh={kh} />
      <div className="bang-cuon" style={{ maxHeight: 320, overflowY: "auto", marginTop: 12 }}>
        <table className="table">
          <thead>
            <tr>
              <th>Thư mục</th>
              <th>File</th>
              <th>Dung lượng</th>
              <th>Lý do</th>
            </tr>
          </thead>
          <tbody>
            {kh.se_xoa.map((m, i) => (
              <tr key={i}>
                <td className="mono small">{m.thu_muc}</td>
                <td className="small" style={{ maxWidth: 320, wordBreak: "break-all" }}>
                  {m.file}
                  {m.so_san_pham > 0 ? (
                    <> <Badge>{m.so_san_pham} SKU</Badge></>
                  ) : null}
                </td>
                <td className="small">{co(m.bytes)}</td>
                <td className="small muted">{m.ly_do}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default function CaiDatPage() {
  const [cd, setCd] = useState<CaiDat | null>(null);
  const [macDinh, setMacDinh] = useState<CaiDat | null>(null);
  const [tk, setTk] = useState<ThongKeKho | null>(null);
  const [kh, setKh] = useState<KeHoachDon | null>(null);
  const [loi, setLoi] = useState<unknown>(null);
  const [dangLuu, setDangLuu] = useState(false);
  const [dangDon, setDangDon] = useState(false);
  const [hoiXoa, setHoiXoa] = useState(false);
  const [daLuu, setDaLuu] = useState(false);

  const nap = () => {
    api
      .caiDat()
      .then((r) => {
        setCd(r.cai_dat);
        setMacDinh(r.mac_dinh);
        setTk(r.thong_ke);
      })
      .catch(setLoi);
  };

  useEffect(nap, []);

  const luu = async () => {
    if (!cd) return;
    setDangLuu(true);
    setLoi(null);
    try {
      const r = await api.luuCaiDat(cd);
      setCd(r.cai_dat);
      setTk(r.thong_ke);
      setDaLuu(true);
      window.setTimeout(() => setDaLuu(false), 2500);
    } catch (e) {
      setLoi(e);
    } finally {
      setDangLuu(false);
    }
  };

  const xemTruoc = async () => {
    setLoi(null);
    try {
      setKh(await api.xemTruocDon(cd?.gioi_han_san_pham));
    } catch (e) {
      setLoi(e);
    }
  };

  const donNgay = async () => {
    setDangDon(true);
    setLoi(null);
    try {
      const r = await api.donNgay(cd?.gioi_han_san_pham);
      setKh(r);
      setTk(r.thong_ke ?? null);
      setHoiXoa(false);
      nap();
    } catch (e) {
      setLoi(e);
      setHoiXoa(false);
    } finally {
      setDangDon(false);
    }
  };

  if (loi && !cd) return <div className="content-pad"><ErrorBox error={loi} /></div>;
  if (!cd || !macDinh) return <div className="content-pad"><Spinner /></div>;

  return (
    <div className="content-pad">
      <ChoPhepCard />

      {tk ? (
        <div className="stat-grid" style={{ marginBottom: 16 }}>
          <div className="stat">
            <div className="sv">{tk.so_san_pham_dang_giu}</div>
            <div className="sl">sản phẩm đang giữ</div>
          </div>
          <div className="stat">
            <div className="sv">{co(tk.tong_bytes)}</div>
            <div className="sl">tổng dung lượng dữ liệu</div>
          </div>
          {Object.entries(tk.thu_muc).map(([ten, v]) => (
            <div className="stat" key={ten}>
              <div className="sv" style={{ fontSize: 17 }}>{co(v.bytes)}</div>
              <div className="sl">{ten} · {v.so_file} file</div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="card">
        <div className="card-head">
          <h2 className="grow">Giới hạn lưu trữ</h2>
          {daLuu ? <Badge tone="ok">Đã lưu</Badge> : null}
        </div>
        <div className="card-body">
          <Alert tone="info">
            Đơn vị giữ là <strong>sản phẩm</strong>, không phải file. Hệ thống duyệt các bộ
            dữ liệu từ mới đến cũ, cộng dồn số SKU; chạm trần thì phần còn lại bị xoá — kèm
            ảnh không còn ai dùng và file nguồn không còn bộ nào trỏ tới. Bộ dữ liệu mới nhất
            luôn được giữ, kể cả khi một mình nó đã vượt trần. Trần áp <strong>riêng</strong>
            cho từng giai đoạn (file_output và file_finish), vì đó là cùng một tập sản phẩm
            ở hai chặng khác nhau.
          </Alert>

          <div className="param-grid" style={{ marginTop: 16 }}>
            <div className="field">
              <label className="label">Giữ tối đa bao nhiêu sản phẩm</label>
              <input
                className="input"
                type="number"
                min={1}
                value={cd.gioi_han_san_pham}
                onChange={(e) =>
                  setCd({ ...cd, gioi_han_san_pham: Number(e.target.value) || 1 })
                }
              />
              <div className="hint">Mặc định {macDinh.gioi_han_san_pham}.</div>
            </div>

            <div className="field">
              <label className="label">Giữ tối đa bao nhiêu nhật ký chạy</label>
              <input
                className="input"
                type="number"
                min={1}
                value={cd.gioi_han_lan_chay}
                onChange={(e) =>
                  setCd({ ...cd, gioi_han_lan_chay: Number(e.target.value) || 1 })
                }
              />
              <div className="hint">Mặc định {macDinh.gioi_han_lan_chay}.</div>
            </div>

            <div className="field param-full">
              <Switch
                checked={cd.tu_dong_don}
                onChange={(v) => setCd({ ...cd, tu_dong_don: v })}
                label="Tự động dọn ngay sau mỗi lần chạy xong"
              />
              <div className="hint">
                Tắt thì phải tự bấm “Dọn ngay”. Mỗi lần dọn đều được ghi vào nhật ký chạy,
                không xoá lặng lẽ.
              </div>
            </div>
          </div>

          {loi ? <div style={{ marginBottom: 12 }}><ErrorBox error={loi} /></div> : null}

          <div className="row">
            <button className="btn btn-primary" onClick={luu} disabled={dangLuu}>
              {dangLuu ? <Spinner /> : null} Lưu cài đặt
            </button>
            <button className="btn" onClick={xemTruoc}>Xem trước sẽ xoá gì</button>
            <button
              className="btn btn-danger"
              onClick={() => setHoiXoa(true)}
              disabled={dangDon}
            >
              {dangDon ? <Spinner /> : null} Dọn ngay
            </button>
          </div>
        </div>
      </div>

      {kh ? (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-head">
            <h2 className="grow">
              {kh.da_xoa ? "Đã dọn" : "Xem trước"}
            </h2>
            {kh.da_xoa ? (
              <Badge tone="ok">
                Xoá {kh.so_file_da_xoa} file · thu hồi {co(kh.bytes_da_thu_hoi ?? 0)}
              </Badge>
            ) : null}
          </div>
          <div className="card-body">
            {kh.da_xoa ? (
              kh.so_file_da_xoa ? (
                <Alert tone="ok">
                  Đã xoá {kh.so_file_da_xoa} file, thu hồi {co(kh.bytes_da_thu_hoi ?? 0)}.
                  {kh.so_nhat_ky_da_xoa ? ` Xoá thêm ${kh.so_nhat_ky_da_xoa} nhật ký chạy cũ.` : ""}
                </Alert>
              ) : (
                <Empty icon="✓">Không có gì cần xoá.</Empty>
              )
            ) : (
              <BangXoa kh={kh} />
            )}
            {kh.that_bai?.length ? (
              <div style={{ marginTop: 12 }}>
                <Alert tone="danger">
                  Không xoá được {kh.that_bai.length} file:
                  <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                    {kh.that_bai.map((m, i) => (
                      <li key={i} className="small">
                        {m.thu_muc}/{m.file} — {m.loi}
                      </li>
                    ))}
                  </ul>
                </Alert>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {hoiXoa && (
        <Modal
          title="Dọn dữ liệu cũ"
          onClose={() => setHoiXoa(false)}
          footer={
            <>
              <button className="btn" onClick={() => setHoiXoa(false)}>Huỷ</button>
              <button className="btn btn-danger" onClick={donNgay} disabled={dangDon}>
                {dangDon ? <Spinner /> : null} Xoá ngay
              </button>
            </>
          }
        >
          <p>
            Sẽ xoá dữ liệu vượt quá <strong>{cd.gioi_han_san_pham} sản phẩm</strong> và các
            nhật ký chạy quá <strong>{cd.gioi_han_lan_chay} lần</strong>.
          </p>
          <Alert tone="warn">
            Xoá file trên đĩa, không hoàn tác được. Bấm “Xem trước sẽ xoá gì” nếu bạn chưa
            chắc danh sách.
          </Alert>
        </Modal>
      )}
    </div>
  );
}
